import React from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Plus,
  X,
  ClipboardCheck,
  CheckCircle2,
  Sparkles,
  Key,
  ShieldCheck,
  Send,
  Lock,
  RefreshCw
} from "lucide-react";

interface BindTelegramAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  loginStep: number;
  setLoginStep: (step: number) => void;
  loginPhone: string;
  setLoginPhone: (phone: string) => void;
  loginCode: string;
  setLoginCode: (code: string) => void;
  loginPassword: string;
  setLoginPassword: (pwd: string) => void;
  loginApiId: string;
  setLoginApiId: (id: string) => void;
  loginApiHash: string;
  setLoginApiHash: (hash: string) => void;
  loginUseOfficialApi: boolean;
  setLoginUseOfficialApi: (val: boolean) => void;
  loginApiMaxAccounts: number;
  setLoginApiMaxAccounts: (val: number) => void;
  showApiPresetPanel: boolean;
  setShowApiPresetPanel: (val: boolean | ((prev: boolean) => boolean)) => void;
  setShowApiGuideModal: (val: boolean) => void;
  smartPasteText: string;
  setSmartPasteText: (txt: string) => void;
  smartPasteFeedback: string;
  setSmartPasteFeedback: (fb: string) => void;
  handleSmartPasteChange: (val: string) => void;
  useProxy: boolean;
  setUseProxy: (val: boolean) => void;
  proxyMode: "random" | "manual";
  setProxyMode: (mode: "random" | "manual") => void;
  manualProxyProtocol: string;
  setManualProxyProtocol: (p: string) => void;
  manualProxyIp: string;
  setManualProxyIp: (ip: string) => void;
  manualProxyPort: string;
  setManualProxyPort: (port: string) => void;
  manualProxyUsername: string;
  setManualProxyUsername: (u: string) => void;
  manualProxyPassword: string;
  setManualProxyPassword: (p: string) => void;
  manualFingerprintUA: string;
  setManualFingerprintUA: (ua: string) => void;
  manualFingerprintDevice: string;
  setManualFingerprintDevice: (dev: string) => void;
  manualFingerprintLang: string;
  setManualFingerprintLang: (lang: string) => void;
  loading: { sendCode: boolean; verifyCode: boolean };
  resendCountdown: number;
  handleSendCode: (e: React.FormEvent) => void;
  handleVerifyCode: (e: React.FormEvent) => void;
  showFeedback: (msg: string, type: "info" | "success" | "error" | "warning") => void;
}

export const BindTelegramAccountModal: React.FC<BindTelegramAccountModalProps> = ({
  isOpen,
  onClose,
  loginStep,
  setLoginStep,
  loginPhone,
  setLoginPhone,
  loginCode,
  setLoginCode,
  loginPassword,
  setLoginPassword,
  loginApiId,
  setLoginApiId,
  loginApiHash,
  setLoginApiHash,
  loginUseOfficialApi,
  setLoginUseOfficialApi,
  loginApiMaxAccounts,
  setLoginApiMaxAccounts,
  showApiPresetPanel,
  setShowApiPresetPanel,
  setShowApiGuideModal,
  smartPasteText,
  setSmartPasteText,
  smartPasteFeedback,
  setSmartPasteFeedback,
  handleSmartPasteChange,
  useProxy,
  setUseProxy,
  proxyMode,
  setProxyMode,
  manualProxyProtocol,
  setManualProxyProtocol,
  manualProxyIp,
  setManualProxyIp,
  manualProxyPort,
  setManualProxyPort,
  manualProxyUsername,
  setManualProxyUsername,
  manualProxyPassword,
  setManualProxyPassword,
  manualFingerprintUA,
  setManualFingerprintUA,
  manualFingerprintDevice,
  setManualFingerprintDevice,
  manualFingerprintLang,
  setManualFingerprintLang,
  loading,
  resendCountdown,
  handleSendCode,
  handleVerifyCode,
  showFeedback
}) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-950/85 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget && !loading.sendCode && !loading.verifyCode) {
            onClose();
          }
        }}
      >
        <motion.div
          initial={{ scale: 0.95, y: 15 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 15 }}
          className="bg-slate-900 border border-sky-500/30 rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden flex flex-col my-8 max-h-[92vh]"
        >
          {/* Modal Header */}
          <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/70">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sky-500/10 border border-sky-500/30 rounded-xl">
                <Plus className="w-5 h-5 text-sky-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  {loginStep === 1 ? "绑定新 Telegram 账号" : "输入验证代码"}
                  {loginStep === 1 && (
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                        !loginApiId.trim() || !loginApiHash.trim()
                          ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                          : "bg-sky-500/10 text-sky-300 border-sky-500/30"
                      }`}
                    >
                      {!loginApiId.trim() || !loginApiHash.trim() ? "普通模式" : "自定义API"}
                    </span>
                  )}
                </h3>
                <p className="text-xs text-slate-400">
                  {loginStep === 1
                    ? "输入手机号以连接官方服务器并获取登录验证码"
                    : `正在验证 ${loginPhone}，请输入收到的登录验证码`}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={loading.sendCode || loading.verifyCode}
              className="p-1.5 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition disabled:opacity-40"
              title="关闭弹窗"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {loginStep === 1 ? (
              <form onSubmit={handleSendCode} className="space-y-4">
                {/* 智能粘贴识别 */}
                <div className="bg-transparent border border-sky-500/30 rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-sky-400 flex items-center gap-1.5">
                      <ClipboardCheck className="w-3.5 h-3.5 text-sky-400" />
                      智能一键识别与自动填入 (可选)
                    </span>
                    {smartPasteText && (
                      <button
                        type="button"
                        onClick={() => {
                          setSmartPasteText("");
                          setSmartPasteFeedback("");
                        }}
                        className="text-[10px] text-slate-400 hover:text-rose-400 underline cursor-pointer"
                      >
                        清空识别框
                      </button>
                    )}
                  </div>
                  <textarea
                    rows={4}
                    placeholder={`直接粘贴任意账号信息文本，将自动识别提取 Phone，亦可包含 api_id, api_hash\n例:\nPhone number：+959778365473\nApp api_id: 38563326 (选填)\nApp api_hash: e473a623d22291e3976c2cdb0ea729f9 (选填)`}
                    value={smartPasteText}
                    onChange={(e) => handleSmartPasteChange(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-transparent border border-slate-800 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-sky-500 font-mono text-xs leading-relaxed resize-none min-h-[96px]"
                  />
                  {smartPasteFeedback && (
                    <div className="text-[11px] text-emerald-400 flex items-center gap-1 font-medium bg-emerald-950/30 p-1.5 rounded border border-emerald-800/40">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      <span className="flex-1">{smartPasteFeedback}</span>
                      <button
                        type="button"
                        onClick={() => setSmartPasteFeedback("")}
                        className="text-slate-500 hover:text-slate-300 ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5 flex items-center justify-between">
                    <span>
                      手机号码 <span className="text-rose-400 font-semibold">*必填</span> (需包含国际区号)
                    </span>
                    <span className="text-[11px] text-slate-500 font-mono">例如: +8613012345678</span>
                  </label>
                  <input
                    type="text"
                    placeholder="+8613012345678"
                    value={loginPhone}
                    onChange={(e) => setLoginPhone(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-sky-500 font-mono text-sm"
                    required
                  />
                </div>

                {/* 登录模式指示器与预设选择器 */}
                <div className="space-y-2">
                  {/* 官方API开关与状态提示 */}
                  <div className="px-3 py-2 rounded-xl bg-slate-950/70 border border-slate-800 text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        id="chkLoginUseOfficialApi"
                        checked={loginUseOfficialApi}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setLoginUseOfficialApi(checked);
                          if (checked) {
                            setLoginApiId("");
                            setLoginApiHash("");
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-sky-500 focus:ring-sky-500 cursor-pointer"
                      />
                      <span className="text-xs font-semibold text-white flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-sky-400" />
                        使用官方API登录
                      </span>
                    </label>

                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${
                          loginUseOfficialApi
                            ? "bg-sky-500/10 text-sky-300 border-sky-500/30"
                            : !loginApiId.trim() || !loginApiHash.trim()
                            ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                            : "bg-amber-500/10 text-amber-300 border-amber-500/30"
                        }`}
                      >
                        {loginUseOfficialApi
                          ? "官方 API (Desktop 2040)"
                          : !loginApiId.trim() || !loginApiHash.trim()
                          ? "凭证池智能打散分配"
                          : "自带 API (池空时自动录入)"}
                      </span>

                      <button
                        type="button"
                        onClick={() => setShowApiPresetPanel((prev) => !prev)}
                        className="text-[11px] text-sky-400 hover:text-sky-300 font-medium cursor-pointer flex items-center gap-1"
                      >
                        <span>{showApiPresetPanel ? "收起预设" : "⚡ 官方预设"}</span>
                      </button>
                      {(loginApiId.trim() || loginApiHash.trim()) && (
                        <button
                          type="button"
                          onClick={() => {
                            setLoginApiId("");
                            setLoginApiHash("");
                          }}
                          className="text-[11px] text-slate-400 hover:text-slate-200 underline cursor-pointer"
                        >
                          清空
                        </button>
                      )}
                    </div>
                  </div>

                  <p className="text-[11px] text-slate-400 px-1 leading-relaxed">
                    {loginUseOfficialApi ? (
                      <span className="text-sky-300">
                        💡 已开启：本次登录将直接使用 Telegram 官方 API (Desktop 2040) 发起验证。
                      </span>
                    ) : (
                      <span>
                        💡 规则：未勾选时优先从您的 API 凭证池分配；<strong>当 API 凭证池为空时，使用您输入的自带 API 凭证登录</strong>并自动收录进池。
                      </span>
                    )}
                  </p>

                  {/* 官方预设快速填入面板 */}
                  {showApiPresetPanel && (
                    <div className="p-3 bg-slate-950 rounded-xl border border-sky-900/40 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-sky-300">一键套用官方客户端凭证：</span>
                        <button
                          type="button"
                          onClick={() => setShowApiGuideModal(true)}
                          className="text-[10px] text-amber-400 hover:text-amber-300 underline flex items-center gap-0.5 cursor-pointer"
                        >
                          <span>📖 申请专属 API 教程</span>
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {[
                          { name: "TDLib 官方 (推荐)", tag: "推荐", id: "94575", hash: "a3406de8d171bb422bb6ddf3bbd800e2" },
                          { name: "Desktop 官方", tag: "桌面", id: "2040", hash: "b1844592fe0a429e5747021d120f4e37" },
                          { name: "Web 网页官方", tag: "网页", id: "2496", hash: "8c8f217937000326305d481a3da31835" },
                          { name: "Android 官方", tag: "安卓", id: "6", hash: "eb06d4abfb49d3eeb1aeb9814400f8dd" },
                          { name: "iOS 苹果官方", tag: "苹果", id: "8", hash: "b92825b2de3d6c04d4ab78a2e35d2f47" },
                        ].map((preset) => {
                          const isCur = loginApiId === preset.id;
                          return (
                            <button
                              key={preset.id}
                              type="button"
                              onClick={() => {
                                setLoginApiId(preset.id);
                                setLoginApiHash(preset.hash);
                                showFeedback(`已自动填入 [${preset.name}] API 凭证 (ID: ${preset.id})`, "info");
                              }}
                              className={`px-2 py-1.5 rounded-lg text-left text-xs border transition flex items-center justify-between cursor-pointer ${
                                isCur
                                  ? "bg-sky-500/20 border-sky-500 text-sky-200 font-bold"
                                  : "bg-slate-900 border-slate-800 text-slate-300 hover:border-slate-700"
                              }`}
                            >
                              <span className="truncate">{preset.name}</span>
                              <span className="text-[9px] px-1 py-0.2 bg-slate-800 text-slate-400 rounded shrink-0 font-mono">
                                {preset.tag}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5 flex items-center justify-between">
                    <span>
                      API ID <span className="text-slate-500 text-[11px] font-normal">(选填 · 留空自动轮询官方端点)</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowApiGuideModal(true)}
                      className="text-[11px] text-amber-400/90 hover:text-amber-300 underline cursor-pointer"
                    >
                      如何获取专属 Key？
                    </button>
                  </label>
                  <input
                    type="text"
                    placeholder="选填，如: 6 或专属 ID (留空自动使用官方客户端)"
                    value={loginApiId}
                    onChange={(e) => setLoginApiId(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-sky-500 font-mono text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5 flex items-center justify-between">
                    <span>
                      API Hash <span className="text-slate-500 text-[11px] font-normal">(选填 · 留空自动使用官方 Hash)</span>
                    </span>
                  </label>
                  <input
                    type="text"
                    placeholder="选填，如: eb06d4abfb49d3eeb1aeb9814400f8dd"
                    value={loginApiHash}
                    onChange={(e) => setLoginApiHash(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-sky-500 font-mono text-sm"
                  />
                </div>

                {/* API 凭证池动态模式提示与挂号上限配置 */}
                {Boolean(loginApiId.trim() && loginApiHash.trim()) ? (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/25 rounded-xl space-y-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-amber-400 flex items-center gap-1.5">
                        <Key className="w-3.5 h-3.5" />
                        自动录入 API 凭证池
                      </span>
                      <span className="text-[10px] px-1.5 py-0.2 rounded font-semibold bg-amber-500/20 text-amber-300">
                        防连坐打散
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-300 leading-relaxed">
                      登录成功后，此套 API ID 将自动加入 API 凭证池。后续登录其他新号时，系统会优先向当前挂号最少的凭据分配负载！
                    </p>
                    <div className="flex items-center justify-between pt-1 border-t border-amber-500/20">
                      <span className="text-slate-300 text-[11px] font-medium">设置此凭证挂号上限:</span>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={loginApiMaxAccounts}
                          onChange={(e) => setLoginApiMaxAccounts(Math.max(1, Number(e.target.value) || 1))}
                          className="w-16 px-2 py-1 text-center bg-slate-900 border border-amber-500/50 rounded-lg text-white font-mono font-bold text-xs"
                        />
                        <span className="text-[11px] text-slate-400">个账号</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-xl space-y-1 text-xs">
                    <div className="flex items-center justify-between text-emerald-400 font-semibold">
                      <span className="flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        API 凭证池负载均衡已就绪
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 font-normal">
                        自动打散
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      系统将自动从 API 凭证池挑选当前挂账号最少的一套 api_id，把号均匀打散；池子为空时回退至全局 API。
                    </p>
                  </div>
                )}

                {/* 安全与隔离配置 */}
                <div className="pt-2 border-t border-slate-800 space-y-3">
                  <label className="flex items-center gap-2.5 cursor-pointer py-1 select-none">
                    <input
                      type="checkbox"
                      checked={useProxy}
                      onChange={(e) => setUseProxy(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-800 text-sky-500 bg-slate-950 focus:ring-sky-500/20 focus:ring-2"
                    />
                    <span className="text-xs font-semibold text-slate-200">
                      启用独立 IP & 浏览器指纹环境
                    </span>
                  </label>

                  {useProxy && (
                    <div className="p-4 bg-slate-950/60 rounded-xl border border-slate-800/80 space-y-3.5">
                      <div>
                        <label className="block text-[11px] font-medium text-slate-400 mb-1.5">
                          IP 分配模式
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setProxyMode("random")}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                              proxyMode === "random"
                                ? "bg-sky-500/10 text-sky-400 border-sky-500/30"
                                : "bg-slate-900/40 text-slate-400 border-slate-800/80 hover:bg-slate-800/40"
                            }`}
                          >
                            资料库随机分配
                          </button>
                          <button
                            type="button"
                            onClick={() => setProxyMode("manual")}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                              proxyMode === "manual"
                                ? "bg-sky-500/10 text-sky-400 border-sky-500/30"
                                : "bg-slate-900/40 text-slate-400 border-slate-800/80 hover:bg-slate-800/40"
                            }`}
                          >
                            手动配置环境
                          </button>
                        </div>
                      </div>

                      {proxyMode === "random" ? (
                        <div className="text-[10px] text-sky-400 leading-relaxed bg-sky-500/5 p-2.5 rounded-lg border border-sky-500/10">
                          💡 系统将自动从【代理与指纹资料库】中随机挑选一个干净可用的代理 IP 与指纹环境分配给该账号，实现登录隔离，防关联封号。
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="grid grid-cols-3 gap-2">
                            <div className="col-span-1">
                              <label className="block text-[10px] text-slate-500 mb-1">协议</label>
                              <select
                                value={manualProxyProtocol}
                                onChange={(e) => setManualProxyProtocol(e.target.value)}
                                className="w-full px-2 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 focus:outline-none focus:border-sky-500 text-xs"
                              >
                                <option value="SOCKS5">SOCKS5</option>
                                <option value="HTTP">HTTP</option>
                              </select>
                            </div>
                            <div className="col-span-2">
                              <label className="block text-[10px] text-slate-500 mb-1">代理 IP</label>
                              <input
                                type="text"
                                placeholder="12.34.56.78"
                                value={manualProxyIp}
                                onChange={(e) => setManualProxyIp(e.target.value)}
                                className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 placeholder-slate-700 focus:outline-none focus:border-sky-500 text-xs font-mono"
                                required={useProxy && proxyMode === "manual"}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-2">
                            <div className="col-span-1">
                              <label className="block text-[10px] text-slate-500 mb-1">端口</label>
                              <input
                                type="number"
                                placeholder="1080"
                                value={manualProxyPort}
                                onChange={(e) => setManualProxyPort(e.target.value)}
                                className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 placeholder-slate-700 focus:outline-none focus:border-sky-500 text-xs font-mono"
                                required={useProxy && proxyMode === "manual"}
                              />
                            </div>
                            <div className="col-span-1">
                              <label className="block text-[10px] text-slate-500 mb-1">账号 (可选)</label>
                              <input
                                type="text"
                                placeholder="user"
                                value={manualProxyUsername}
                                onChange={(e) => setManualProxyUsername(e.target.value)}
                                className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 placeholder-slate-700 focus:outline-none focus:border-sky-500 text-xs"
                              />
                            </div>
                            <div className="col-span-1">
                              <label className="block text-[10px] text-slate-500 mb-1">密码 (可选)</label>
                              <input
                                type="password"
                                placeholder="pass"
                                value={manualProxyPassword}
                                onChange={(e) => setManualProxyPassword(e.target.value)}
                                className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 placeholder-slate-700 focus:outline-none focus:border-sky-500 text-xs"
                              />
                            </div>
                          </div>

                          <div className="pt-2 border-t border-slate-800/60 space-y-2">
                            <div className="text-[10px] font-semibold text-slate-400">指纹环境模拟</div>
                            <div>
                              <label className="block text-[10px] text-slate-500 mb-1">User Agent</label>
                              <input
                                type="text"
                                placeholder="Mozilla/5.0..."
                                value={manualFingerprintUA}
                                onChange={(e) => setManualFingerprintUA(e.target.value)}
                                className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 placeholder-slate-700 focus:outline-none focus:border-sky-500 text-xs font-mono"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-[10px] text-slate-500 mb-1">设备型号</label>
                                <input
                                  type="text"
                                  placeholder="Windows PC"
                                  value={manualFingerprintDevice}
                                  onChange={(e) => setManualFingerprintDevice(e.target.value)}
                                  className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 placeholder-slate-700 focus:outline-none focus:border-sky-500 text-xs"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] text-slate-500 mb-1">系统语言</label>
                                <input
                                  type="text"
                                  placeholder="zh-CN"
                                  value={manualFingerprintLang}
                                  onChange={(e) => setManualFingerprintLang(e.target.value)}
                                  className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 placeholder-slate-700 focus:outline-none focus:border-sky-500 text-xs"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading.sendCode}
                  className="w-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold py-2.5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-sm shadow-lg shadow-sky-500/25 disabled:opacity-50 cursor-pointer active:scale-95"
                >
                  {loading.sendCode ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      正在与 Telegram 建立连接...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 text-slate-950" />
                      下一步：请求登录验证码
                    </>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div className="p-3 bg-sky-500/5 rounded-xl border border-sky-500/20 text-xs text-sky-400">
                  正在登录账号: <span className="font-semibold font-mono text-white">{loginPhone}</span>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5 flex items-center justify-between">
                    <span>请输入 Telegram 验证码 (Verification Code)</span>
                    {resendCountdown > 0 ? (
                      <span className="text-[11px] text-slate-500 font-mono">
                        重新获取 ({resendCountdown}s)
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          handleSendCode(e);
                        }}
                        disabled={loading.sendCode}
                        className="text-[11px] text-sky-400 hover:text-sky-300 font-medium transition-colors cursor-pointer"
                      >
                        {loading.sendCode ? "正在重发..." : "重新获取验证码"}
                      </button>
                    )}
                  </label>
                  <input
                    type="text"
                    placeholder="例如: 12345"
                    value={loginCode}
                    onChange={(e) => setLoginCode(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-sky-500 font-mono text-sm text-center tracking-widest font-semibold"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5 flex items-center justify-between">
                    <span>两步验证密码 (若开启了 2FA 请填写)</span>
                    <Lock className="w-3.5 h-3.5 text-slate-500" />
                  </label>
                  <input
                    type="password"
                    placeholder="请输入您的 Telegram 密码"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-sky-500 text-sm"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setLoginStep(1)}
                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-slate-300 font-medium py-2.5 rounded-xl transition-all duration-200 text-sm border border-slate-800 cursor-pointer active:scale-95"
                  >
                    返回修改
                  </button>
                  <button
                    type="submit"
                    disabled={loading.verifyCode}
                    className="flex-[2] bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-2.5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-sm shadow-lg shadow-emerald-500/25 cursor-pointer active:scale-95"
                  >
                    {loading.verifyCode ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        正在验证并创建Session...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-slate-950" />
                        确认提交并登录
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
